<template>
  <div class="masonry-layout">
    <div
      v-for="(columnData, columnIndex) in dataByColumns"
      :key="`column-${columnIndex}`"
      ref="columnsEls"
      class="masonry-layout__column"
    >
      <div
        v-for="(item, itemIndex) in columnData"
        :key="`item-${itemIndex}`"
        class="masonry-layout__item"
      >
        <slot :item="item" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, defineProps, onMounted, ref, watch } from 'vue';

const props = defineProps<{
  data: any[];
  columnsCount: number;
}>();

const columnsEls = ref<HTMLElement[]>([]);

const dataByColumns = ref<any[][]>([]);

const columnWidth = computed(() => `calc(100% / ${props.columnsCount})`);

let dataIndex = 0;
let animationFrame: number;

function getAllItemsFromColumns(): any[] {
  return dataByColumns.value.flat();
}

function shouldResetLayout(newData: any[]): boolean {
  const currentItems = getAllItemsFromColumns();
  // Проверяем, есть ли элементы в текущей раскладке, которых нет в новых данных
  return currentItems.some(item => !newData.includes(item));
}

async function appendNextItem() {
  if (dataIndex >= props.data.length) {
    cancelAnimationFrame(animationFrame);
    return;
  }

  const nextItem = props.data[dataIndex];
  // Проверяем, есть ли уже этот элемент в колонках
  const isItemAlreadyPlaced = getAllItemsFromColumns().includes(nextItem);

  if (isItemAlreadyPlaced) {
    // Если элемент уже размещен, пропускаем его
    dataIndex += 1;
    animationFrame = requestAnimationFrame(() => {
      appendNextItem();
    });

    return;
  }

  let columnWithLesserHeightIndex = 0;

  for (let i = 0; i < columnsEls.value.length; i++) {
    const columnEl = columnsEls.value[i];
    const columnHeight = columnEl.offsetHeight;

    if (columnHeight < columnsEls.value[columnWithLesserHeightIndex].offsetHeight) {
      columnWithLesserHeightIndex = i;
    }
  }

  dataByColumns.value[columnWithLesserHeightIndex].push(nextItem);
  dataIndex += 1;

  animationFrame = requestAnimationFrame(() => {
    appendNextItem();
  });
}

function startDataAppend() {
  if (animationFrame) {
    cancelAnimationFrame(animationFrame);
  }

  // Если dataByColumns пустой или нужен полный сброс - инициализируем массивы
  if (dataByColumns.value.length === 0 || shouldResetLayout(props.data)) {
    dataByColumns.value = Array.from({ length: props.columnsCount }).map(() => []);
    dataIndex = 0;
  } else {
    // Иначе начинаем с индекса последнего обработанного элемента
    const existingItems = getAllItemsFromColumns();
    dataIndex = existingItems.length;
  }

  animationFrame = requestAnimationFrame(() => {
    appendNextItem();
  });
}

onMounted(startDataAppend);
watch(() => props.data.length, startDataAppend);
watch(() => props.columnsCount, startDataAppend);
</script>

<style>
.masonry-layout {
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.masonry-layout__column {
  width: v-bind(columnWidth);
  display: flex;
  flex-direction: column;
  gap: 15px;
}
</style>
